import React, { FC, useEffect, useState } from 'react';

import cx from 'clsx';
import { Icon } from '@mdi/react';

import {
  AlarmCallEvent,
  AlarmCallEventAudioPhase,
  AlarmCallEventContentType,
  AlarmCallParticipant,
} from '@x-guard/xgac-types/xgac';
import { useSelector } from 'react-redux';
import { useLocale } from 'lib';
import {
  mdiAccount, mdiIncognito, mdiMicrophone, mdiVolumeMute,
} from '@mdi/js';
import { PopulatedAlarmHelper } from 'types';
import { Image } from 'components/molecules';
import { heartbeatMs, openCallParticipantsSelector, stateSelector as chatStateSelector } from 'features/details/chat';
import { resultSelector as helpersSelector } from 'features/details/helpers';
import { userContextSelector } from 'features/identity/auth';
import { usePersonHighlight } from '../usePersonHighlight';
import styles from './style.module.scss';

type SelfUser = { userId?: string; name: string };

export type CallPerson = {
  id: string;
  /** Asset / user id, to recognise the same person in the other lists. */
  personId?: string;
  name: string;
  image?: string | null;
  anonymous: boolean;
  /** The operator looking at the screen. */
  self?: boolean;
  connectedAt?: string;
  muted: boolean;
  speaking: boolean;
};

const initials = (name: string) => name
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0]?.toUpperCase() ?? '')
  .join('');

const isConnected = (participant: AlarmCallParticipant) => (
  !!participant.state.connectedAt && !participant.state.disconnectedAt
);

const assetIdOf = (participant: AlarmCallParticipant) => (
  participant.ref?._ref === 'Asset' ? participant.ref._id : undefined
);

const isSpeaking = (participant: AlarmCallParticipant, callEvents: AlarmCallEvent[], now: number) => {

  if (!isConnected(participant) || participant.state.muted) return false;

  const liveAudio = callEvents.some((event) => {

    if (event.participant !== participant._id) return false;
    if (event.content.type !== AlarmCallEventContentType.Audio) return false;
    if (event.content.state.phase !== AlarmCallEventAudioPhase.Started) return false;

    const elapsed = now - Date.parse(event.content.lastHeartbeatAt);

    return elapsed < heartbeatMs;

  });

  return liveAudio || participant.state.speaking;

};

const formatConnected = (connectedAt: string, now: number) => {

  const totalSeconds = Math.max(0, Math.floor((now - Date.parse(connectedAt)) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (value: number) => String(value).padStart(2, '0');

  if (hours > 0) return `${pad(hours)}:${pad(minutes)}`;

  return `${pad(minutes)}:${pad(seconds)}`;

};

export const visibleCallPeople = (
  helpers: PopulatedAlarmHelper[],
  participants: AlarmCallParticipant[],
  callEvents: AlarmCallEvent[],
  now: number,
  anonymousName: string,
  selfUser: SelfUser,
): CallPerson[] => {

  const connected = participants.filter(isConnected);
  const usedParticipantIds = new Set<string>();

  const fromHelpers = helpers
    .filter((helper) => helper.confirmedHelping === true)
    .map((helper) => {

      const helperAssetId = helper.asset?._id;
      const participant = helperAssetId
        ? connected.find((item) => assetIdOf(item) === helperAssetId)
        : undefined;

      if (participant) usedParticipantIds.add(participant._id);

      return {
        id: helper._id,
        personId: helper.asset?._id,
        name: helper.asset?.name ?? anonymousName,
        image: helper.asset?.properties?.image,
        anonymous: !helper.asset,
        connectedAt: participant?.state.connectedAt ?? undefined,
        muted: !!participant?.state.muted,
        speaking: participant ? isSpeaking(participant, callEvents, now) : false,
      };

    });

  const fromCall = connected
    .filter((participant) => !usedParticipantIds.has(participant._id))
    .map((participant) => {

      const helper = helpers.find((item) => item.asset?._id && item.asset._id === assetIdOf(participant));
      const self = participant.ref?._ref === 'User' && participant.ref._id === selfUser.userId;

      return {
        id: participant._id,
        personId: participant.ref?._id,
        name: (self && selfUser.name) || helper?.asset?.name || participant.name,
        image: self ? undefined : helper?.asset?.properties?.image,
        anonymous: false,
        self,
        connectedAt: participant.state.connectedAt ?? undefined,
        muted: participant.state.muted,
        speaking: isSpeaking(participant, callEvents, now),
      };

    });

  return [...fromHelpers, ...fromCall].sort((left, right) => {

    const rank = (person: CallPerson) => {

      if (person.self) return -1;
      if (person.connectedAt && person.speaking) return 0;
      if (person.connectedAt) return 1;

      return 2;

    };

    return rank(left) - rank(right) || left.name.localeCompare(right.name);

  });

};

/** Everyone to show as helping: confirmed helpers plus anyone connected to an open helper call (you first). */
export const useCallPeople = (now: number): CallPerson[] => {

  const { t } = useLocale();
  const helpers = useSelector(helpersSelector);
  const { callEvents } = useSelector(chatStateSelector);
  const participants = useSelector(openCallParticipantsSelector);
  const user = useSelector(userContextSelector)?.user;

  return visibleCallPeople(helpers, participants, callEvents, now, t('Helpers_Anonymous'), {
    userId: user?._id,
    name: user?.name ?? t('Alarm_Detail_DCC.Phone_Call.Operator'),
  });

};

const PersonCard: FC<{ person: CallPerson; now: number }> = ({ person, now }) => {

  const { t } = useLocale();
  const { highlighted, hoverProps } = usePersonHighlight(person.personId);

  return (

    <div
      className={cx(styles.card, person.self && styles.self, person.speaking && styles.speaking, highlighted && styles.highlighted)}
      {...hoverProps}
    >
      <div className={styles.avatarWrap}>
        {person.anonymous && (
          <div className={styles.avatar}><Icon path={mdiIncognito} size="22px"/></div>
        )}
        {!person.anonymous && !person.self && person.image && (
          <Image src={person.image} alt="" className={styles.avatar}/>
        )}
        {!person.anonymous && !person.image && (
          <div className={styles.avatar}>
            {(!person.self && initials(person.name)) || <Icon path={mdiAccount} size="22px"/>}
          </div>
        )}
        {person.connectedAt && person.muted && (
          <span className={styles.mutedBadge}><Icon path={mdiVolumeMute} size="12px"/></span>
        )}
        {person.speaking && (
          <span className={styles.micBadge}><Icon path={mdiMicrophone} size="12px"/></span>
        )}
        {!person.speaking && person.connectedAt && (
          <span className={styles.connectionBadge}>{formatConnected(person.connectedAt, now)}</span>
        )}
      </div>
      <div className={styles.cardText}>
        <span className={styles.cardName} title={person.name}>{person.name}</span>
        {person.self && <span className={styles.selfLabel}>{t('Alarm_Detail_DCC.Connected.Self')}</span>}
      </div>
    </div>

  );

};

export const HelpersList: FC = () => {

  const { t } = useLocale();
  const [now, setNow] = useState(() => Date.now());
  const people = useCallPeople(now);
  const callIsLive = people.some((person) => person.connectedAt);

  useEffect(() => {

    if (!callIsLive) return undefined;

    const timer = window.setInterval(() => setNow(Date.now()), 1000);

    return () => window.clearInterval(timer);

  }, [callIsLive]);

  return (
    <div className={cx(styles.helpersContainer, people.length === 0 && styles.noHelpers)}>
      {people.length === 0 && t('Helpers_None')}
      {people.map((person) => (
        <PersonCard
          key={person.id}
          person={person}
          now={now}
        />
      ))}
    </div>
  );

};
